import React from "react";
import { ModalsKeys } from "global";
import { useSelector } from "react-redux";
import { useModal } from "hooks/useModal";
import { useHistory } from "react-router-dom";
import { useNotifications } from "modules/notification";
import { PageContainer } from "components/shared/Page/PageContainer";
import { generateProposalPageURL } from "utils/generateProposalPageURL";
import { FormSections, DeskingSubmitOptions } from "modules/desking/types";
import { CardContainer } from "components/shared/CardContainer/CardContainer";
import { CreateLeadProposalResponse, CreateLeadProposalVariables } from "modules/startDeal/api/types";
import { DeskingPanel, collectVariablesForCreateProposal } from "modules/desking";
import {
  LeadSection,
  ApiContainer,
  EquipmentSection,
  collectEquipmentDTO,
  ProposalCreationModal,
  generateCreateLeadProposalLeadDTO,
} from "modules/startDeal";

const modals = {
  [ModalsKeys.ProposalCreationDialog]: <ProposalCreationModal />,
};

export const StartDealPage = () => {
  const { push } = useHistory();
  const { showNotification } = useNotifications();
  const { handleOpen: handleOpenLoadingModal, handleClose: handleCloseLoadingModal } = useModal(
    ModalsKeys.ProposalCreationDialog
  );

  const account = useSelector((state: any) => state.account);
  const userProfile = useSelector((state: any) => state.userProfile);

  const handleSubmitDesking = async (
    options: DeskingSubmitOptions,
    onCreateLead: (input: CreateLeadProposalVariables["input"]) => Promise<CreateLeadProposalResponse["leadProposal"]>
  ) => {
    const { form, builder, commonProductsConfiguration, productsConfigInMenu, isDraft, pricing } = options;

    if (!form.contact.email || !form.contact.fullName) {
      showNotification("Lead Email and Contact required", { type: "error" });
      return;
    }

    handleOpenLoadingModal();

    const {
      input: { proposalMenuId, financeProgramId, saleType, settings, paymentOptions },
    } = collectVariablesForCreateProposal({
      isDraft,
      pricing,
      voId: "",
      formData: form,
      menuByTerms: builder,
      productsConfigInMenu,
      commonProductsConfiguration,
    });

    const { success, id, vendorGUID, vendorOpportunityId } = await onCreateLead({
      equipmentDTO: collectEquipmentDTO(form.equipment),
      leadDTO: generateCreateLeadProposalLeadDTO(form.contact),
      proposalDTO: { financeProgramId, paymentOptions, proposalMenuId, saleType, settings },
    });

    handleCloseLoadingModal();

    if (success) {
      showNotification("Proposal created!");
      push(`/vo/${vendorOpportunityId}`);

      const linkToProposalPage = generateProposalPageURL(id!, vendorGUID!);
      window.open(linkToProposalPage, "_blank");
    } else {
      showNotification("Something went wrong", { type: "error" });
    }
  };

  return (
    <PageContainer modals={modals}>
      <CardContainer>
        <ApiContainer>
          {startDealRenderProps => (
            <DeskingPanel
              accountId={account.id}
              isSupportSaleDetailsUpdating
              userEmail={userProfile.email}
              isProposalLoading={startDealRenderProps.isProposalLoading}
              customSections={{
                [FormSections.Contact]: <LeadSection sectionName={FormSections.Contact} {...startDealRenderProps} />,
                [FormSections.Equipment]: (
                  <EquipmentSection sectionName={FormSections.Equipment} {...startDealRenderProps} />
                ),
              }}
              onSubmitDesking={async options => {
                await handleSubmitDesking(options, startDealRenderProps.handleCreateLeadProposal);
              }}
            />
          )}
        </ApiContainer>
      </CardContainer>
    </PageContainer>
  );
};
